//
// We need to add a screen at the start to get users to login to their accounts if they already exist
// Also add a login button to the top right corner of the screen
//

import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Card, Button, Input, Select, Modal, Tag, Avatar, Divider, Progress, message, Spin, Empty, Badge, Alert, Space, Radio } from "antd";
import { CreditCardOutlined, PlusOutlined, CheckCircleOutlined, StopOutlined } from "@ant-design/icons";

import { Appointment, AppointmentSaveRequest, AppointmentType, AvailablePractitioner, AvailableDay, TimeSlot, BookingPolicy } from "../../types/appointments/AppointmentTypes";
import { fetchAvailableTimes, createAppointment, createAppointmentViaLink, getBookingLinkContext, fetchBookableAppointmentTypesPatient, BookingLinkContext } from "../../api/healthcare/Appointment/AppointmentApi";
import { fetchAppointmentTypesPatientAll } from "../../api/healthcare/Appointment/AppointmentSettingsApi";
import { isWithinBookingWindow } from "../../utils/healthcare/bookingWindow";
import { classifyBookingLinkError, BookingLinkState } from "../../utils/healthcare/bookingLinkState";
import { SCREENING_REQUIRED } from "../../utils/healthcare/screeningOutcome";
import { generateConsultationLink } from "../../utils/consult/consultationLink";
import { ScreeningPass } from "../../types/appointments/ScreeningTypes";
import ScreeningStep from "../../components/booking/ScreeningStep";
import { DateTimeUtils, parseUTCToLocal, formatLocalTime } from "../../utils/userPrefs/DateTimeUtils";
import { createPaymentPublic, listPaymentMethodsPublic, createFinanceCustomerPublic } from "../../api/finance/FinanceApi";
import { checkFinanceIntegrationForCompany } from "../../api/admin/integrationApi";
import { PaymentStatus, PaymentType, StripeCustomer, StripePaymentMethod } from "../../types/finance/FinanceTypes";
import AddPaymentMethodModal from "../../components/finance/AddPaymentMethodModal";
import BrandLogo from "../../components/branding/BrandLogo";
import PoweredBy from "../../components/branding/PoweredBy";
import { useBranding } from "../../context/BrandingContext";
import "./BookingForm.css";

export interface PatientInfo {
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
}

interface AggregatedAvailability {
	date: string;
	practitioners: Array<{
		practitioner: AvailablePractitioner;
		slots: TimeSlot[];
	}>;
}

const formatLocalDateString = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const isDateToday = (date: Date): boolean => {
	const today = new Date();
	return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
};

const parseLocalDateString = (dateStr: string): Date => {
	const [year, month, day] = dateStr.split("-").map(Number);
	return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const useQuery = (): URLSearchParams => new URLSearchParams(useLocation().search);

const PatientBookingForm: React.FC = () => {
	const [messageApi, contextHolder] = message.useMessage();

	// S8 (86d3w1g8u) — this page is PUBLIC and pre-auth, which is exactly why branding comes
	// from `BrandingProvider` (mounted above every route) rather than from the session.
	const { branding } = useBranding();
	// ⚠️ Fall back to "Xoe Med", not to `display_name`. A whitelabelled clinic that uploaded a
	// logo but never set a name would otherwise render "© null" in the footer.
	const brandDisplayName = branding.whitelabel && branding.display_name ? branding.display_name : "Xoe Med";

	const query = useQuery();

	/**
	 * The booking link's capability (S9, ClickUp 86d3yhdw9). Its presence is what turns this
	 * page from the anonymous booking form into a per-patient one — and it is the ONLY thing
	 * that is allowed to change this screen's behaviour, because an anonymous visitor uses the
	 * very same route.
	 */
	const accessToken = query.get("t") || undefined;

	// ── Tokenised booking link state ───────────────────────────────────────────────────────
	const [linkContext, setLinkContext] = useState<BookingLinkContext | null>(null);
	const [linkState, setLinkState] = useState<BookingLinkState>("ok");
	/**
	 * Starts TRUE. The first paint happens before the bootstrap effect resolves, and with
	 * `false` that frame falls straight through to the wizard — an unlocked, empty, wrong-step
	 * form flashing on a link that is perfectly good, and (once the context read fails) an
	 * invalid-link screen flashing on one that is not. `Form.tsx:69-71` carries the same note
	 * for the same trap.
	 *
	 * ⚠️ NOT the existing `isLoading` below, which means "loading available times" and is
	 * flipped every time an appointment type is chosen. Initialising THAT to `true` would spin
	 * forever on the anonymous path, where nothing calls `loadAvailableTimes()` until the
	 * visitor picks a type.
	 */
	const [isLoadingLink, setIsLoadingLink] = useState<boolean>(true);

	/**
	 * The link's appointment type enters the SAME deep-link path `?appType=` already uses,
	 * rather than a second pre-selection mechanism beside it: one effect, one code path, and a
	 * scoped link behaves exactly like a deep link that also knows who the patient is.
	 * An explicit `?appType=` still wins — it is the more specific instruction on the URL.
	 */
	const urlAppType = query.get("appType");
	const initialAppType = urlAppType ?? (linkContext?.appointment_type_id != null ? String(linkContext.appointment_type_id) : null);

	// State management
	const [bookingFlow, setBookingFlow] = useState<"consultant" | "time" | null>(null);
	const [currentStep, setCurrentStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [error, setError] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);

	// Appointment types
	const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
	const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);
	const [loadingTypes, setLoadingTypes] = useState(false);

	// Profile modal
	const [showProfileModal, setShowProfileModal] = useState(false);
	const [profileConsultant, setProfileConsultant] = useState<AvailablePractitioner | null>(null);
	// Finance's booking capability for `financeCustomer` (contract §16.5): memory only, never stored.
	const [bookingPaymentToken, setBookingPaymentToken] = useState<string | null>(null);

	// Payment state
	const [financeEnabled, setFinanceEnabled] = useState(false);
	const [financeCustomer, setFinanceCustomer] = useState<StripeCustomer | null>(null);
	const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([]);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
	const [addPaymentModalVisible, setAddPaymentModalVisible] = useState(false);
	const [loadingPayment, setLoadingPayment] = useState(false);
	const [paymentIntentId, setPaymentIntentId] = useState<string>("");
	const [paymentRefreshTrigger, setPaymentRefreshTrigger] = useState(0);

	// Form data
	const [patientInfo, setPatientInfo] = useState<PatientInfo>({
		first_name: "",
		last_name: "",
		email: "",
		phone: "",
	});

	const [appointmentData, setAppointmentData] = useState<Partial<AppointmentSaveRequest>>({
		status: "Pending",
		location: "",
		category: "",
		title: "Patient Booking",
		date: undefined,
		start_time: undefined,
		end_time: undefined,
		practitioner_id: "",
		appointment_type_id: undefined,
		consultation_link: "",
	});

	const categories = [
		{ label: "Yes", value: "Established Patient" },
		{ label: "No", value: "New Patient" },
	];

	const [practitioners, setPractitioners] = useState<AvailablePractitioner[]>([]);
	const [selectedPractitioner, setSelectedPractitioner] = useState<AvailablePractitioner | null>(null);
	const [selectedDate, setSelectedDate] = useState<string>("");
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
	const [availableDays, setAvailableDays] = useState<AvailableDay[]>([]);
	const [availableTimesForDate, setAvailableTimesForDate] = useState<TimeSlot[]>([]);

	// The clinic's PUBLIC booking policy (timezone + booking_window_days), returned by the booking
	// read. This form is unauthenticated and cannot call the settings endpoint, so this block is the
	// only place the clinic's booking window and clinic timezone are knowable here.
	const [bookingPolicy, setBookingPolicy] = useState<BookingPolicy | null>(null);

	// Time-first flow specific state
	const [aggregatedAvailability, setAggregatedAvailability] = useState<AggregatedAvailability[]>([]);
	const [filteredPractitioners, setFilteredPractitioners] = useState<AvailablePractitioner[]>([]);

	// Calendar navigation
	const [currentMonth, setCurrentMonth] = useState(new Date());

	// Expanded appointment type description
	const [expandedTypeId, setExpandedTypeId] = useState<number | null>(null);

	// Pre-booking screening (S8, ClickUp 86d439wvy). The pass is per appointment type and lives
	// only here — it is a short-lived capability and a patient may be on a shared device.
	const [screeningToken, setScreeningToken] = useState<string | undefined>(undefined);
	const [screeningBlocked, setScreeningBlocked] = useState<string | null>(null);
	const [screeningResumeStep, setScreeningResumeStep] = useState<number | null>(null);

	const hasScreeningStep = Boolean(selectedAppointmentType?.has_screening);
	const screeningRequired = hasScreeningStep && !screeningToken;
	// Screening is inserted for DISPLAY at index 1; `currentStep` keeps the numbering every
	// existing call site was written against, so no downstream index shifts.
	const displayStep = !hasScreeningStep || currentStep === 0 ? currentStep : screeningRequired ? 1 : currentStep + 1;

	/**
	 * Bootstrap. On a tokenised visit the link context is read BEFORE anything else and the
	 * rest of the page is driven from it; a refused link stops here and nothing further is
	 * requested, because every one of those calls would be pointless work on a dead end.
	 *
	 * An anonymous visit takes the same path it always did — one `loadAppointmentTypes()` and
	 * `initializeFinance()`, no extra request.
	 */
	useEffect(() => {
		const load = async () => {
			let context: BookingLinkContext | null = null;

			if (accessToken) {
				try {
					context = await getBookingLinkContext(accessToken);
				} catch (err: any) {
					setLinkState(classifyBookingLinkError(err?.response?.status, err?.response?.data?.code));
					setIsLoadingLink(false);
					return;
				}

				setLinkContext(context);
				// Every field is nullable — an auth-service outage yields nulls rather than a
				// 5xx, and an empty locked field is a better outcome than a page that will not
				// render because a prefill lookup failed.
				setPatientInfo({
					first_name: context?.patient?.first_name || "",
					last_name: context?.patient?.last_name || "",
					email: context?.patient?.email || "",
					phone: context?.patient?.phone || "",
				});
			}

			await loadAppointmentTypes(Boolean(urlAppType) || context?.appointment_type_id != null);
			initializeFinance();
			setIsLoadingLink(false);
		};

		load();
	}, []);

	// Handle initialAppType from URL after appointmentTypes are loaded
	useEffect(() => {
		if (initialAppType && appointmentTypes.length > 0) {
			console.log("Initial appointment type from URL:", initialAppType);
			const matchedType = appointmentTypes.find((type) => type.id.toString() === initialAppType);
			if (matchedType) {
				console.log("Matched appointment type:", matchedType);
				// Auto-select the appointment type
				handleAppointmentTypeSelect(matchedType);
				// Set booking flow to consultant since we're coming from a direct link
				setBookingFlow("consultant");
				// Skip the appointment type selection step (step 0) and flow selection
				setCurrentStep(1);
			} else {
				console.log("No matching appointment type found for:", initialAppType);
			}
		}
	}, [appointmentTypes, initialAppType]);

	/**
	 * A link that also names a practitioner pre-selects them once availability has loaded, by
	 * calling the SAME handler a click on their card calls — so the wizard advances exactly as
	 * it would have, and nothing about the two flows is forked. Silently ignored when the named
	 * practitioner has no availability for the type: the patient then picks from whoever does,
	 * which beats an empty consultant step with no explanation.
	 */
	useEffect(() => {
		const linkPractitionerId = linkContext?.practitioner_id;
		if (!linkPractitionerId || selectedPractitioner || practitioners.length === 0) return;

		const matched = practitioners.find((p) => p.practitioner_id === linkPractitionerId);
		if (matched) {
			handleConsultantSelect(matched);
		}
	}, [practitioners, linkContext, selectedPractitioner]);

	useEffect(() => {
		if (selectedPractitioner) {
			setAvailableDays(selectedPractitioner.Available_times);
			if (bookingFlow === "consultant") {
				setSelectedDate("");
				setSelectedTimeSlot(null);
				setAvailableTimesForDate([]);
			}
			setAppointmentData((prev) => ({
				...prev,
				practitioner_id: selectedPractitioner.practitioner_id,
			}));
		} else {
			if (bookingFlow === "consultant") {
				setAvailableDays([]);
				setSelectedDate("");
				setSelectedTimeSlot(null);
				setAvailableTimesForDate([]);
			}
		}
	}, [selectedPractitioner]);

	useEffect(() => {
		if (bookingFlow === "consultant" && selectedDate && availableDays.length > 0) {
			const dayData = availableDays.find((day) => day.date === selectedDate);
			if (dayData) {
				setAvailableTimesForDate(dayData.slots);
				setAppointmentData((prev) => ({
					...prev,
					date: parseLocalDateString(selectedDate),
				}));
			} else {
				setAvailableTimesForDate([]);
			}
			setSelectedTimeSlot(null);
			setAppointmentData((prev) => ({
				...prev,
				start_time: undefined,
				end_time: undefined,
			}));
		} else if (bookingFlow === "time" && selectedDate && aggregatedAvailability.length > 0) {
			const dayData = aggregatedAvailability.find((day) => day.date === selectedDate);
			if (dayData) {
				const allSlots: TimeSlot[] = [];
				dayData.practitioners.forEach((p) => {
					allSlots.push(...p.slots);
				});
				const uniqueSlots = Array.from(new Map(allSlots.map((slot) => [slot.time, slot])).values());
				setAvailableTimesForDate(uniqueSlots);
				setAppointmentData((prev) => ({
					...prev,
					date: parseLocalDateString(selectedDate),
				}));
			} else {
				setAvailableTimesForDate([]);
			}
			if (!selectedTimeSlot) {
				setAppointmentData((prev) => ({
					...prev,
					start_time: undefined,
					end_time: undefined,
				}));
			}
		}
	}, [selectedDate, availableDays, aggregatedAvailability, bookingFlow]);

	useEffect(() => {
		if (selectedTimeSlot && selectedDate && selectedAppointmentType) {
			const startTime = parseUTCToLocal(selectedTimeSlot.datetime);

			if (startTime) {
				const endTime = new Date(startTime);
				endTime.setMinutes(endTime.getMinutes() + selectedAppointmentType.duration_minutes);

				setAppointmentData((prev) => ({
					...prev,
					start_time: startTime,
					end_time: endTime,
					date: startTime,
				}));
			}

			if (bookingFlow === "time") {
				const availablePractitioners = practitioners.filter((practitioner) => {
					return practitioner.Available_times.some((day) => day.date === selectedDate && day.slots.some((slot) => slot.time === selectedTimeSlot.time));
				});
				setFilteredPractitioners(availablePractitioners);
			}
		} else if (bookingFlow === "time") {
			setFilteredPractitioners([]);
		}
	}, [selectedTimeSlot, selectedDate, bookingFlow, practitioners, selectedAppointmentType]);

	useEffect(() => {
		if (bookingFlow === "time" && practitioners.length > 0) {
			aggregateAvailability();
		}
	}, [bookingFlow, practitioners]);

	useEffect(() => {
		if (paymentRefreshTrigger > 0 && financeCustomer) {
			loadPaymentMethods();
		}
	}, [paymentRefreshTrigger]);

	const initializeFinance = async () => {

		try {
			//stripe is the only integration, but in future we would be checking which integration a user has active
			const { isActive } = await checkFinanceIntegrationForCompany();
			setFinanceEnabled(Boolean(isActive));
		} catch (error) {
			console.error("Error initializing Stripe:", error);
		}
	};

	const createTheFinanceCustomer = async () => {
		if (!financeEnabled|| !patientInfo.email) return;

		setLoadingPayment(true);

		try {
			const created = await createFinanceCustomerPublic({
				userId: patientInfo.email, // Using email as temp identifier
				email: patientInfo.email,
				name: `${patientInfo.first_name} ${patientInfo.last_name}`,
				phone: patientInfo.phone,
			});

			setFinanceCustomer(created.customer);
			setBookingPaymentToken(created.paymentToken);
		} catch (createError: any) {
			console.error("Error with Stripe customer:", createError);
			const errorMessage = createError.response?.data?.message || createError.message;
			if (createError.response?.status === 422) {
				messageApi.error(errorMessage);
			} else {
				messageApi.warning(errorMessage || "Something went wrong. Please check your details and try again.");
			}
			throw createError;
		} finally {
			setLoadingPayment(false);
		}
	};

	const loadPaymentMethods = async (customerId?: string) => {
		const custId = customerId || financeCustomer?.id;
		if (!custId || !bookingPaymentToken) return;

		try {
			const methods = await listPaymentMethodsPublic(custId, bookingPaymentToken);
			setPaymentMethods(methods);

			// Auto-select default payment method if available
			const defaultMethod = methods.find((m) => m.id === financeCustomer?.invoice_settings?.default_payment_method);
			if (defaultMethod) {
				setSelectedPaymentMethod(defaultMethod.id);
			}
		} catch (error) {
			console.error("Error loading payment methods:", error);
		}
	};

	/**
	 * @param includeNonBookable A type named by a deep link OR by a booking link's scope may not
	 *   be in the patient-bookable list at all — a practitioner issuing a link for it has
	 *   already made that decision. Passed in rather than read off `initialAppType` because on
	 *   the tokenised path the link context has not been committed to state yet when this runs.
	 */
	const loadAppointmentTypes = async (includeNonBookable: boolean) => {
		setLoadingTypes(true);
		try {
			// If we have an initialAppType from URL, fetch ALL appointment types (not just bookable ones for patients)
			// This allows direct linking to any appointment type, even ones not normally bookable by patients
			if (includeNonBookable) {
				const types = await fetchAppointmentTypesPatientAll();
				setAppointmentTypes(types.filter(type => type.is_active));
			} else {
				const types = await fetchBookableAppointmentTypesPatient();
				setAppointmentTypes(types);
			}
		} catch (error) {
			console.error("Error loading appointment types:", error);
			messageApi.error("Failed to load appointment types");
		} finally {
			setLoadingTypes(false);
		}
	};

	const handleAppointmentTypeSelect = async (type: AppointmentType) => {
		// The pass is issued per appointment type, so switching type discards it.
		const heldPass = screeningToken && selectedAppointmentType?.id === type.id ? screeningToken : undefined;
		setScreeningToken(heldPass);

		setSelectedAppointmentType(type);
		setAppointmentData((prev) => ({
			...prev,
			appointment_type_id: type.id,
			location: type.telehealth_enabled ? "online" : "in-person",
			title: type.name,
		}));

		// FOUR paths advance past step 0, so the gate sits on the availability read they all
		// depend on rather than on any one of them — every path then lands on the screening step.
		if (type.has_screening && !heldPass) return;

		await loadAvailableTimes(type.id, heldPass);
	};

	const aggregateAvailability = () => {
		const availabilityMap = new Map<string, AggregatedAvailability>();

		practitioners.forEach((practitioner) => {
			practitioner.Available_times.forEach((day) => {
				if (!availabilityMap.has(day.date)) {
					availabilityMap.set(day.date, {
						date: day.date,
						practitioners: [],
					});
				}
				const dayAvailability = availabilityMap.get(day.date)!;
				dayAvailability.practitioners.push({
					practitioner,
					slots: day.slots,
				});
			});
		});

		const aggregated = Array.from(availabilityMap.values()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
		setAggregatedAvailability(aggregated);
	};

	const loadAvailableTimes = async (appointmentTypeId: number, screeningPass?: string) => {
		setIsLoading(true);
		try {
			// Third argument only when a pass is held, so a non-screened type's call is unchanged.
			const data = screeningPass ? await fetchAvailableTimes(appointmentTypeId, accessToken, screeningPass) : await fetchAvailableTimes(appointmentTypeId, accessToken);
			setPractitioners(data.practitioners);
			// Present on both 200 paths, absent on the 404 — never assume it is there.
			setBookingPolicy(data.policy ?? null);

			if (data.practitioners.length === 0) {
				messageApi.warning("No practitioners available for this appointment type");
			}
		} catch (error) {
			console.error("Error loading available times:", error);
			setError("Failed to load available appointment times. Please try again.");
			messageApi.error("Failed to load available times");
		} finally {
			setIsLoading(false);
		}
	};

	const requiresPayment = (): boolean => {
		if (!selectedAppointmentType?.default_price) return false;
		const price = parseFloat(selectedAppointmentType.default_price);
		return price > 0;
	};

	const processPayment = async (): Promise<boolean> => {
		if (!requiresPayment()) return true;

		if (!selectedPaymentMethod) {
			messageApi.error("Please select a payment method");
			return false;
		}

		if (!financeCustomer || !bookingPaymentToken) {
			messageApi.error("Payment setup incomplete");
			return false;
		}

		setLoadingPayment(true);
		try {
			// Stripe expects amount in cents, so multiply by 100 and do not round the dollars
			const amount = Math.floor(parseFloat(selectedAppointmentType!.default_price!) * 100);

			const payment = await createPaymentPublic({
				type: PaymentType.BOOKING,
				amount: amount,
				currency: "AUD",
				paymentMethodId: selectedPaymentMethod,
				description: `${selectedAppointmentType!.name} - ${patientInfo.first_name} ${patientInfo.last_name}`,
				bookingId: `booking-${crypto.randomUUID()}`,
				metadata: {
					appointment_type: selectedAppointmentType!.name,
					patient_name: `${patientInfo.first_name} ${patientInfo.last_name}`,
					patient_email: patientInfo.email,
				},
			}, bookingPaymentToken);

			setPaymentIntentId(payment.metadata.stripe_payment_intent_id);

			if (payment.status === PaymentStatus.COMPLETED) {
				message.success("Payment processed successfully");
				return true;
			} else {
				message.error("Payment requires additional action");
				return false;
			}

		} catch (error: any) {
			console.error("Payment error:", error);
			messageApi.error(error.message || "Payment failed");
			return false;
		} finally {
			setLoadingPayment(false);
		}
	};

	const handleFlowSelect = (flow: "consultant" | "time") => {
		setBookingFlow(flow);
		setCurrentStep(1);
		setSelectedPractitioner(null);
		setSelectedDate("");
		setSelectedTimeSlot(null);
		setAvailableDays([]);
		setAvailableTimesForDate([]);
		setFilteredPractitioners([]);
	};

	const goToStep = (step: number) => {
		setCurrentStep(step);
		setError("");
	};

	const handleScreeningPass = async (pass: ScreeningPass) => {
		setScreeningToken(pass.screening_token);
		setError("");

		await loadAvailableTimes(selectedAppointmentType!.id, pass.screening_token);

		if (screeningResumeStep !== null) {
			setCurrentStep(screeningResumeStep);
			setScreeningResumeStep(null);
		}
	};

	// The blocked screen's only exit. Everything the previous type set has to go, or the next
	// type inherits its practitioners, slots and pass.
	const resetScreening = () => {
		setScreeningBlocked(null);
		setScreeningToken(undefined);
		setScreeningResumeStep(null);
		setSelectedAppointmentType(null);
		setBookingFlow(null);
		setCurrentStep(0);
		setError("");
		setPractitioners([]);
		setSelectedPractitioner(null);
		setSelectedDate("");
		setSelectedTimeSlot(null);
		setAvailableDays([]);
		setAvailableTimesForDate([]);
		setAggregatedAvailability([]);
		setFilteredPractitioners([]);
		setExpandedTypeId(null);
		setAppointmentData((prev) => ({
			...prev,
			appointment_type_id: undefined,
			practitioner_id: "",
			date: undefined,
			start_time: undefined,
			end_time: undefined,
			title: "Patient Booking",
		}));
	};

	const handleConsultantSelect = (practitioner: AvailablePractitioner) => {
		setSelectedPractitioner(practitioner);
		if (bookingFlow === "consultant") {
			goToStep(2);
		} else if (bookingFlow === "time") {
			goToStep(3);
		}
	};

	const handleDateSelect = (date: string) => {
		setSelectedDate(date);
		setSelectedTimeSlot(null);
	};

	const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
		const enhancedTimeSlot = {
			...timeSlot,
			localDateTime: DateTimeUtils.parseUTCToLocal(timeSlot.datetime),
			displayTime: DateTimeUtils.formatLocalTime(timeSlot.datetime),
		};
		setSelectedTimeSlot(enhancedTimeSlot);

		if (bookingFlow === "consultant") {
			setTimeout(() => goToStep(3), 300);
		}
	};

	const canContinueToPatientInfo = () => {
		return selectedPractitioner && selectedDate && selectedTimeSlot && appointmentData.category && selectedAppointmentType;
	};

	const canContinueToPayment = () => {
		return patientInfo.first_name && patientInfo.last_name && patientInfo.email && appointmentData.category;
	};

	const handleContinueFromPatientInfo = async () => {
		if (!canContinueToPayment()) {
			messageApi.warning("Please fill in all required fields");
			return;
		}

		if (requiresPayment() && financeEnabled) {
			try {
				await createTheFinanceCustomer();
				goToStep(bookingFlow === "consultant" ? 4 : 4);
			} catch {
				// Error already displayed — stay on patient info step
			}
		} else {
			goToStep(bookingFlow === "consultant" ? 4 : 4);
		}
	};

	const handleSubmit = async () => {
		setError("");

		if (!patientInfo.first_name || !patientInfo.last_name || !patientInfo.email) {
			setError("Please fill in all patient information fields");
			messageApi.warning("Please fill in all required fields");
			return;
		}

		if (!appointmentData.practitioner_id || !appointmentData.date || !appointmentData.start_time) {
			setError("Please complete all appointment details");
			return;
		}

		if (!appointmentData.category || !appointmentData.appointment_type_id) {
			setError("Please complete all appointment details");
			return;
		}

		// Process payment if required
		if (requiresPayment() && financeEnabled) {
			const paymentSuccess = await processPayment();
			if (!paymentSuccess) {
				setError("Payment failed. Please try again.");
				return;
			}
		}

		if (selectedAppointmentType?.telehealth_enabled) {
			appointmentData.consultation_link = generateConsultationLink();
		}

		try {
			setIsSubmitting(true);

			//TODO: The user should have already been made by this point so will refactor the appointment endpoint

			// The tokenised POST sends NO patient_* fields: `bookAppointmentViaLink()` takes
			// identity from the link and never consults the body for it. That — not the locked
			// inputs — is what makes the per-patient cap unbeatable by typing another name.
			const result = accessToken
				? await createAppointmentViaLink(appointmentData, accessToken, screeningToken)
				: await createAppointment(appointmentData, patientInfo, screeningToken);
			if (result.data.success) {
				setIsSubmitted(true);

				messageApi.success("Appointment booked successfully!");
			}
		} catch (err: any) {
			// A pass that expired while the patient browsed. Send them back to the questionnaire
			// rather than dead-ending on a review screen they can never submit.
			const body = err?.response?.data || {};
			if (err?.response?.status === 422 && (body.error === SCREENING_REQUIRED || body.code === SCREENING_REQUIRED)) {
				setScreeningToken(undefined);
				setScreeningResumeStep(currentStep);
				setCurrentStep(1);
				setError(body.message || "Your screening answers have expired. Please answer the questions again.");
				return;
			}

			// A replayed link (410 `already_booked`) is not an error the patient can act on by
			// retrying — it is a terminal state, and it is reassurance. Everything else,
			// including the 422 cap refusal that carries the SAME message string, stays an
			// inline error on a form the patient can still use.
			const state = accessToken ? classifyBookingLinkError(err?.response?.status, err?.response?.data?.code) : "ok";
			if (state !== "ok") {
				setLinkState(state);
				return;
			}

			const reason = err?.response?.data?.message || err.message;
			setError(reason || "An error occurred while booking your appointment");
			messageApi.error(reason || "Failed to book appointment");
		} finally {
			setIsSubmitting(false);
		}
	};

	const openProfileModal = (practitioner: AvailablePractitioner) => {
		setProfileConsultant(practitioner);
		setShowProfileModal(true);
	};

	const closeProfileModal = () => {
		setShowProfileModal(false);
		setProfileConsultant(null);
	};

	const bookFromProfile = () => {
		if (profileConsultant) {
			handleConsultantSelect(profileConsultant);
			closeProfileModal();
		}
	};

	const getAvailableDates = () => {
		if (bookingFlow === "consultant" && selectedPractitioner) {
			return selectedPractitioner.Available_times.map((day) => day.date);
		} else if (bookingFlow === "time") {
			return aggregatedAvailability.map((day) => day.date);
		}
		return [];
	};

	const getStepsForFlow = () => {
		const flowSteps = bookingFlow === "consultant" ? [{ label: "Type" }, { label: "Consultant" }, { label: "Date & Time" }, { label: "Your Info" }] : [{ label: "Type" }, { label: "Date & Time" }, { label: "Consultant" }, { label: "Your Info" }];

		const baseSteps = hasScreeningStep ? [flowSteps[0], { label: "Screening" }, ...flowSteps.slice(1)] : flowSteps;

		if (requiresPayment() && financeEnabled) {
			return [...baseSteps, { label: "Payment" }, { label: "Review" }];
		}

		return [...baseSteps, { label: "Review" }];
	};

	const getPractitionerCountForTimeSlot = (slot: TimeSlot): number => {
		if (bookingFlow !== "time" || !selectedDate) return 0;

		const dayData = aggregatedAvailability.find((day) => day.date === selectedDate);
		if (!dayData) return 0;

		return dayData.practitioners.filter((p) => p.slots.some((s) => s.time === slot.time)).length;
	};

	const renderProgressBar = () => {
		const steps = getStepsForFlow();
		const progress = (displayStep / steps.length) * 100;

		return (
			<Card className="progress-card">
				<div className="progress-steps">
					{steps.map((step, index) => (
						<div key={index} className="progress-step">
							<div className={`step-circle ${index === displayStep ? "active" : ""} ${index < displayStep ? "completed" : ""}`}>{index < displayStep ? "✓" : index + 1}</div>
							<span className={`step-label ${index === displayStep ? "active" : ""}`}>{step.label}</span>
						</div>
					))}
				</div>
				<Progress percent={progress} showInfo={false} className="progress-bar-custom" />
			</Card>
		);
	};

	const renderErrorCard = () =>
		error ? (
			<Card className="error-card">
				<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
					<i className="fas fa-exclamation-circle" />
					<span>{error}</span>
				</div>
			</Card>
		) : null;

	const renderScreeningStep = () => (
		<>
			{renderProgressBar()}

			{renderErrorCard()}

			<div className="booking-main">
				<div className="booking-selection">
					<ScreeningStep
						appointmentTypeId={selectedAppointmentType!.id}
						bookingToken={accessToken}
						onPass={handleScreeningPass}
						// The fallback fires only when the backend sent no message — a blank terminal card tells the patient nothing.
						onBlocked={(reason) => setScreeningBlocked(reason || "Please contact the clinic to book this appointment.")}
					/>
				</div>
			</div>
		</>
	);

	const renderAppointmentTypeSelection = () => (
		<div className="selection-section" style={{ fontSize: "0.95rem" }}>
			<h2 className="section-title" style={{ fontSize: "1.2rem" }}>
				Select Appointment Type
			</h2>
			<p className="section-subtitle" style={{ fontSize: "0.95rem" }}>
				Choose the type of appointment you need
			</p>

			{loadingTypes ? (
				<div className="loading-state">
					<Spin size="large" />
					<p style={{ marginTop: "1rem" }}>Loading appointment types...</p>
				</div>
			) : appointmentTypes.length === 0 ? (
				<Card style={{ textAlign: "center", padding: "2rem" }}>
					<Empty description="No appointment types available" />
				</Card>
			) : (
				<div
					className="appointment-types-list"
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
						gap: "0.75rem",
					}}
				>
					{appointmentTypes.map((type) => {
						const isExpanded = expandedTypeId === type.id;
						const price = type.default_price ? parseFloat(type.default_price) : 0;
						return (
							<Card
								key={type.id}
								className={`appointment-type-card ${selectedAppointmentType?.id === type.id ? "selected-appointment-type" : ""}`}
								style={{
									cursor: "pointer",
									transition: "all 0.3s",
									fontSize: "0.95rem",
									minHeight: 110,
								}}
							>
								<div
									onClick={() => {
										handleAppointmentTypeSelect(type);
										setExpandedTypeId(isExpanded ? null : type.id);
									}}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.75rem",
									}}
								>
									<div style={{ flex: 1 }}>
										<h3 style={{ margin: 0, marginBottom: "0.25rem", fontSize: "1rem" }}>
											{selectedAppointmentType?.id === type.id && <i className="fas fa-check-circle" data-testid="booking-type-selected-icon" style={{ color: "var(--primary)", marginRight: "0.4rem" }} />}
											{type.name}
										</h3>
										<div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
											<Tag color="blue" style={{ fontSize: "0.85em", padding: "0 6px" }}>
												{type.duration_minutes} min
											</Tag>
											{type.telehealth_enabled && (
												<Tag color="green" style={{ fontSize: "0.85em", padding: "0 6px" }}>
													Online
												</Tag>
											)}
											{price > 0 && (
												<Tag color="gold" style={{ fontSize: "0.85em", padding: "0 6px" }}>
													${price.toFixed(2)}
												</Tag>
											)}
										</div>
									</div>
									{type.description && (
										<div
											onClick={(e) => {
												e.stopPropagation();
												setExpandedTypeId(isExpanded ? null : type.id);
											}}
											style={{
												padding: "0.25rem",
												cursor: "pointer",
												color: "var(--text-secondary)",
												transition: "transform 0.2s",
												transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
												fontSize: "1rem",
											}}
										>
											<i className="fas fa-chevron-right" />
										</div>
									)}
								</div>
								{type.description && isExpanded && (
									<div
										style={{
											marginTop: "0.5rem",
											paddingTop: "0.5rem",
											borderTop: "1px solid var(--border-color)",
											fontSize: "0.85rem",
											color: "var(--text-secondary)",
										}}
									>
										{type.description}
									</div>
								)}
							</Card>
						);
					})}
				</div>
			)}

			{selectedAppointmentType && !isLoading && (practitioners.length > 0 || screeningRequired) && (
				<div style={{ marginTop: "1.5rem", textAlign: "center" }}>
					<Button type="primary" size="middle" style={{ fontSize: "1rem", padding: "0.4rem 1.5rem" }} onClick={() => goToStep(1)}>
						Continue <i className="fas fa-arrow-right" style={{ marginLeft: "0.5rem" }} />
					</Button>
				</div>
			)}

			<style>
				{`
				@media (max-width: 600px) {
					.appointment-types-list {
						grid-template-columns: 1fr !important;
					}
					.selection-section {
						font-size: 0.98rem !important;
					}
				}
				`}
			</style>
		</div>
	);

	const renderFlowSelection = () => (
		<div className="flow-selection-container">
			<Card className="flow-selection-card">
				<h2>How would you like to book?</h2>
				<div className="flow-options-grid">
					<Card className="flow-option-card" onClick={() => handleFlowSelect("consultant")}>
						<div className="flow-icon">
							<i className="fas fa-user-doctor" />
						</div>
						<h3>Choose Consultant First</h3>
						<p>Select your preferred consultant, then pick from their available times</p>
					</Card>
					<Card className="flow-option-card" onClick={() => handleFlowSelect("time")}>
						<div className="flow-icon">
							<i className="fas fa-calendar-check" />
						</div>
						<h3>Choose Time First</h3>
						<p>Select your preferred date and time, then see available consultants</p>
					</Card>
				</div>
			</Card>
		</div>
	);

	const renderConsultantCard = (practitioner: AvailablePractitioner, isCompact: boolean = false) => {
		const isSelected = selectedPractitioner?.practitioner_id === practitioner.practitioner_id;

		let availableSlots = 0;
		let availabilityText = "";

		if (bookingFlow === "consultant") {
			availableSlots = practitioner.Available_times.reduce((sum, day) => sum + day.slots.length, 0);
			if (availableSlots >= 5) {
				availabilityText = "Good availability";
			} else if (availableSlots > 0) {
				availabilityText = "Limited availability";
			} else {
				availabilityText = "No availability";
			}
		} else if (bookingFlow === "time" && selectedDate && selectedTimeSlot) {
			const isAvailable = practitioner.Available_times.some((day) => day.date === selectedDate && day.slots.some((slot) => slot.time === selectedTimeSlot.time));
			availabilityText = isAvailable ? "Available for selected time" : "Not available for selected time";
		}

		if (isCompact) {
			return (
				<Card key={practitioner.practitioner_id} className={`consultant-card-compact ${isSelected ? "selected" : ""}`} onClick={() => handleConsultantSelect(practitioner)} style={{ marginBottom: "0.75rem" }}>
					<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
						<Avatar src={practitioner.profile_picture || "/assets/images/default-profile.png"} size={48} />
						<div style={{ flex: 1, minWidth: 0 }}>
							<h4 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600 }}>
								{practitioner.first_name} {practitioner.last_name}
							</h4>
							<p style={{ color: "var(--text-secondary)", fontSize: "0.8rem", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{practitioner.primary_specialty}</p>
						</div>
						<Button
							type="primary"
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleConsultantSelect(practitioner);
							}}
						>
							Select
						</Button>
					</div>
				</Card>
			);
		}

		return (
			<Card key={practitioner.practitioner_id} className={`consultant-card ${isSelected ? "selected" : ""}`} onClick={() => handleConsultantSelect(practitioner)}>
				<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
					<Avatar src={practitioner.profile_picture || "/assets/images/default-profile.png"} size={60} />
					<div style={{ flex: 1 }}>
						<h3>
							{practitioner.first_name} {practitioner.last_name}
						</h3>
						<p style={{ color: "var(--text-secondary)", fontSize: "0.875rem", margin: 0 }}>{practitioner.primary_specialty}</p>
					</div>
				</div>

				{practitioner.specialties && practitioner.specialties.length > 0 && (
					<div className="consultant-specialties">
						{practitioner.specialties.slice(0, 3).map((spec, idx) => (
							<Tag key={idx}>{spec}</Tag>
						))}
					</div>
				)}

				{availabilityText && (
					<div className="consultant-availability">
						<i className="fas fa-check-circle" />
						<span>{availabilityText}</span>
					</div>
				)}

				<div className="consultant-actions">
					<Button
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							openProfileModal(practitioner);
						}}
						style={{ color: "black" }}
					>
						<i className="fas fa-user" /> Meet
					</Button>
					<Button type="primary" size="small">
						<i className="fas fa-calendar-check" /> Book
					</Button>
				</div>
			</Card>
		);
	};

	const renderConsultantSelection = () => {
		const practitionersToShow = bookingFlow === "time" && filteredPractitioners.length > 0 ? filteredPractitioners : practitioners;

		return (
			<div className="selection-section">
				<h2 className="section-title">{bookingFlow === "time" ? "Available Consultants" : "Choose Your Consultant"}</h2>
				<p className="section-subtitle">
					{bookingFlow === "time" ? `Showing ${practitionersToShow.length} consultant${practitionersToShow.length !== 1 ? "s" : ""} available at your selected time` : "Select a consultant based on your needs and their availability"}
				</p>

				{isLoading ? (
					<div className="loading-state">
						<i className="fas fa-spinner fa-spin" style={{ fontSize: "2rem", color: "var(--primary)" }} />
						<p style={{ marginTop: "1rem" }}>Loading consultants...</p>
					</div>
				) : practitionersToShow.length === 0 ? (
					<Card style={{ textAlign: "center", padding: "3rem" }}>
						<i className="fas fa-calendar-times" style={{ fontSize: "3rem", color: "var(--text-secondary)", marginBottom: "1rem" }} />
						<h3>No consultants available</h3>
						<p style={{ color: "var(--text-secondary)" }}>Please try selecting a different time slot or appointment type</p>
						<Button style={{ marginTop: "1rem" }} onClick={() => goToStep(bookingFlow === "time" ? 1 : 0)}>
							{bookingFlow === "time" ? "Change Time" : "Change Appointment Type"}
						</Button>
					</Card>
				) : (
					<div className="consultant-grid">{practitionersToShow.map((p) => renderConsultantCard(p, false))}</div>
				)}
			</div>
		);
	};

	const renderCalendar = () => {
		const availableDates = getAvailableDates();

		if (bookingFlow === "consultant" && !selectedPractitioner) {
			return (
				<Card style={{ textAlign: "center", padding: "3rem" }}>
					<i className="fas fa-user" style={{ fontSize: "3rem", color: "var(--text-secondary)", marginBottom: "1rem" }} />
					<h3>Select a consultant first</h3>
					<p style={{ color: "var(--text-secondary)" }}>Please go back and choose your preferred consultant</p>
				</Card>
			);
		}

		if (bookingFlow === "time" && availableDates.length === 0) {
			return (
				<Card style={{ textAlign: "center", padding: "3rem" }}>
					<i className="fas fa-spinner fa-spin" style={{ fontSize: "2rem", color: "var(--primary)" }} />
					<p style={{ marginTop: "1rem" }}>Loading available dates...</p>
				</Card>
			);
		}

		const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
		const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
		const startDate = new Date(monthStart);
		startDate.setDate(startDate.getDate() - startDate.getDay());

		const days = [];
		let day = new Date(startDate);

		while (day <= monthEnd || day.getDay() !== 0) {
			const dateStr = formatLocalDateString(day);
			// The clinic's booking window bounds the calendar: a date past `booking_window_days` is not
			// selectable even if a stale slot list still carries it.
			const isAvailable = availableDates.includes(dateStr) && isWithinBookingWindow(day, bookingPolicy);
			const isSelected = dateStr === selectedDate;
			const isToday = isDateToday(day);
			const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

			let slotsCount = 0;
			if (bookingFlow === "consultant" && selectedPractitioner) {
				const dayData = selectedPractitioner.Available_times.find((d) => d.date === dateStr);
				slotsCount = dayData?.slots.length || 0;
			} else if (bookingFlow === "time") {
				const dayData = aggregatedAvailability.find((d) => d.date === dateStr);
				if (dayData) {
					const allSlots = new Set<string>();
					dayData.practitioners.forEach((p) => {
						p.slots.forEach((slot) => allSlots.add(slot.time));
					});
					slotsCount = allSlots.size;
				}
			}

			days.push(
				<div
					key={dateStr}
					className={`calendar-day ${isSelected ? "selected" : ""} ${!isAvailable || !isCurrentMonth ? "unavailable" : ""} ${isToday ? "today" : ""}`}
					onClick={() => isAvailable && isCurrentMonth && handleDateSelect(dateStr)}
				>
					<span className="calendar-day-number">{day.getDate()}</span>
					{isAvailable && isCurrentMonth && <span className="calendar-day-slots">{slotsCount} slots</span>}
				</div>
			);

			day.setDate(day.getDate() + 1);
			if (days.length >= 42) break;
		}

		return (
			<div className="date-picker">
				<div className="calendar-nav">
					<Button size="small" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
						<i className="fas fa-chevron-left" />
					</Button>
					<h3>{currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h3>
					<Button size="small" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
						<i className="fas fa-chevron-right" />
					</Button>
				</div>
				<div className="calendar-grid">
					{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
						<div key={day} className="calendar-header">
							{day}
						</div>
					))}
					{days}
				</div>
			</div>
		);
	};

	const renderDateTimeSelection = () => (
		<div className="selection-section">
			<h2 className="section-title">Select Date & Time</h2>
			<p className="section-subtitle">{bookingFlow === "time" ? "Choose your preferred appointment date and time from all available slots" : "Choose from your consultant's available times"}</p>

			{renderCalendar()}

			<div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
				<Button
					onClick={() => {
						if (bookingFlow === "consultant") {
							goToStep(1);
						} else {
							goToStep(0);
						}
					}}
				>
					<i className="fas fa-arrow-left" /> Back
				</Button>
			</div>
		</div>
	);

	/**
	 * Identity is the link's, not the visitor's — so the four fields it supplies are locked.
	 *
	 * ⚠️ GATED ON THE TOKEN, NEVER UNCONDITIONAL: anonymous visitors use this exact screen and
	 * must keep typing their own details.
	 *
	 * ⚠️ AND THIS IS A UI COURTESY, NOT THE CONTROL. `bookAppointmentViaLink()` reads identity
	 * from `$link->patient_id` and never from the request body, so editing these in devtools
	 * changes nothing server-side. Locking them stops the honest patient from believing the
	 * name they type is the name that gets booked.
	 */
	const isIdentityLocked = Boolean(accessToken);

	const setPatientField = (field: keyof PatientInfo, value: string) => {
		if (isIdentityLocked) return;
		setPatientInfo((prev) => ({ ...prev, [field]: value }));
	};

	const renderPatientForm = () => (
		<div className="selection-section">
			<h2 className="section-title">Your Information</h2>
			<p className="section-subtitle">{isIdentityLocked ? "These details came with your booking link" : "Please provide your contact details"}</p>

			<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }}>
				<div className="field">
					<label htmlFor="firstName" className="form-label">
						First Name *
					</label>
					<Input id="firstName" data-testid="booking-first-name" readOnly={isIdentityLocked} value={patientInfo.first_name} onChange={(e) => setPatientField("first_name", e.target.value)} />
				</div>

				<div className="field">
					<label htmlFor="lastName" className="form-label">
						Last Name *
					</label>
					<Input id="lastName" data-testid="booking-last-name" readOnly={isIdentityLocked} value={patientInfo.last_name} onChange={(e) => setPatientField("last_name", e.target.value)} />
				</div>

				<div className="field">
					<label htmlFor="email" className="form-label">
						Email Address *
					</label>
					<Input id="email" data-testid="booking-email" type="email" readOnly={isIdentityLocked} value={patientInfo.email} onChange={(e) => setPatientField("email", e.target.value)} />
				</div>

				<div className="field">
					<label htmlFor="phone" className="form-label">
						Phone Number *
					</label>
					<Input id="phone" data-testid="booking-phone" type="tel" readOnly={isIdentityLocked} value={patientInfo.phone} onChange={(e) => setPatientField("phone", e.target.value)} />
				</div>

				<div className="field">
					<label htmlFor="category" className="form-label">
						Have you booked with us before *
					</label>
					<Select
						id="category"
						value={appointmentData.category}
						options={categories.map((c) => ({ label: c.label, value: c.value }))}
						onChange={(value) => setAppointmentData((prev) => ({ ...prev, category: value }))}
						placeholder="Select option"
						style={{ width: "100%" }}
					/>
				</div>
			</div>

			{selectedAppointmentType && (
				<Card className="appointment-type-info" style={{ marginTop: "1.5rem" }}>
					<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
						<i className={`fas ${selectedAppointmentType.telehealth_enabled ? "fa-video" : "fa-building"}`} style={{ fontSize: "2rem", color: selectedAppointmentType.telehealth_enabled ? "#3b82f6" : "#10b981" }} />
						<div>
							<h4>{selectedAppointmentType.telehealth_enabled ? "Video Consultation" : "In-person Visit"}</h4>
							<p style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
								{selectedAppointmentType.telehealth_enabled ? "You will receive a video link before your appointment" : "Please arrive 15 minutes early for your appointment"}
							</p>
						</div>
					</div>
				</Card>
			)}

			<div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
				<Button onClick={() => goToStep(bookingFlow === "consultant" ? 2 : 2)}>
					<i className="fas fa-arrow-left" /> Back
				</Button>
				<Button type="primary" onClick={handleContinueFromPatientInfo} disabled={!canContinueToPatientInfo()} loading={loadingPayment}>
					{requiresPayment() && financeEnabled ? "Continue to Payment" : "Review Booking"} <i className="fas fa-arrow-right" />
				</Button>
			</div>
		</div>
	);

	const renderPaymentSection = () => {
		const price = selectedAppointmentType?.default_price ? parseFloat(selectedAppointmentType.default_price) : 0;

		return (
			<div className="selection-section">
				<h2 className="section-title">Payment</h2>
				<p className="section-subtitle">Complete your payment to confirm the booking</p>

				{loadingPayment ? (
					<div style={{ textAlign: "center", padding: "3rem" }}>
						<Spin size="large" />
						<p style={{ marginTop: "1rem" }}>Setting up payment...</p>
					</div>
				) : (
					<>
						<Card style={{ marginBottom: "1.5rem" }}>
							<Space direction="vertical" size="small" style={{ width: "100%" }}>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<span style={{ fontSize: "1rem", fontWeight: 500 }}>Appointment Fee</span>
									<span style={{ fontSize: "1.25rem", fontWeight: 600, color: "var(--primary)" }}>${price.toFixed(2)}</span>
								</div>
								<Divider style={{ margin: "0.75rem 0" }} />
								<div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
									<div>Type: {selectedAppointmentType?.name}</div>
									<div>Duration: {selectedAppointmentType?.duration_minutes} minutes</div>
								</div>
							</Space>
						</Card>

						{!financeCustomer ? (
							<Alert message="Setting up your payment account..." type="info" showIcon />
						) : (
							<>
								<h3 style={{ fontSize: "1rem", marginBottom: "1rem" }}>Select Payment Method</h3>

								{paymentMethods.length === 0 ? (
									<Card style={{ textAlign: "center", padding: "2rem", marginBottom: "1rem" }}>
										<CreditCardOutlined style={{ fontSize: "3rem", color: "var(--text-secondary)", marginBottom: "1rem" }} />
										<p>No payment methods available</p>
										<Button type="primary" icon={<PlusOutlined />} onClick={() => setAddPaymentModalVisible(true)}>
											Add Payment Method
										</Button>
									</Card>
								) : (
									<>
										<Radio.Group value={selectedPaymentMethod} onChange={(e) => setSelectedPaymentMethod(e.target.value)} style={{ width: "100%", marginBottom: "1rem" }}>
											<Space direction="vertical" size="middle" style={{ width: "100%" }}>
												{paymentMethods.map((method) => (
													<Card
														key={method.id}
														className={`payment-method-card ${selectedPaymentMethod === method.id ? "selected" : ""}`}
														style={{ cursor: "pointer" }}
														onClick={() => setSelectedPaymentMethod(method.id)}
													>
														<Radio value={method.id} style={{ width: "100%" }}>
															<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
																<CreditCardOutlined style={{ fontSize: "1.5rem" }} />
																<div style={{ flex: 1 }}>
																	<div style={{ fontWeight: 500 }}>
																		{method.card?.brand.toUpperCase()} •••• {method.card?.last4}
																	</div>
																	<div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
																		Expires {method.card?.exp_month}/{method.card?.exp_year}
																	</div>
																</div>
																{financeCustomer?.invoice_settings?.default_payment_method === method.id && (
																	<Tag color="blue" icon={<CheckCircleOutlined />}>
																		Default
																	</Tag>
																)}
															</div>
														</Radio>
													</Card>
												))}
											</Space>
										</Radio.Group>

										<Button icon={<PlusOutlined />} onClick={() => setAddPaymentModalVisible(true)} style={{ width: "100%" }}>
											Add New Payment Method
										</Button>
									</>
								)}
							</>
						)}
					</>
				)}

				<div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
					<Button onClick={() => goToStep(3)}>
						<i className="fas fa-arrow-left" /> Back
					</Button>
					<Button type="primary" onClick={() => goToStep(requiresPayment() && financeEnabled ? 5 : 4)} disabled={!selectedPaymentMethod || loadingPayment}>
						Review Booking <i className="fas fa-arrow-right" />
					</Button>
				</div>

				{financeCustomer && (
					<AddPaymentMethodModal
						visible={addPaymentModalVisible}
						onClose={() => setAddPaymentModalVisible(false)}
						onSuccess={() => {
							setAddPaymentModalVisible(false);
							setPaymentRefreshTrigger((prev) => prev + 1);
						}}
						customerId={financeCustomer.id}
						paymentToken={bookingPaymentToken ?? undefined}
					/>
				)}
			</div>
		);
	};

	const renderConfirmation = () => {
		const price = selectedAppointmentType?.default_price ? parseFloat(selectedAppointmentType.default_price) : 0;
		const selectedPaymentMethodDetails = paymentMethods.find((m) => m.id === selectedPaymentMethod);

		return (
			<div className="selection-section">
				<h2 className="section-title">Review Your Booking</h2>
				<p className="section-subtitle">Please confirm your appointment details</p>

				<Card className="confirmation-card">
					{selectedAppointmentType && (
						<>
							<div className="confirmation-item">
								<span className="confirmation-label">Appointment Type</span>
								<span className="confirmation-value">{selectedAppointmentType.name}</span>
							</div>
							<Divider />
						</>
					)}
					<div className="confirmation-item">
						<span className="confirmation-label">Consultant</span>
						<span className="confirmation-value">
							{selectedPractitioner?.first_name} {selectedPractitioner?.last_name}
						</span>
					</div>
					<Divider />
					<div className="confirmation-item">
						<span className="confirmation-label">Date</span>
						<span className="confirmation-value">
							{appointmentData.date &&
								DateTimeUtils.formatLocalDate(appointmentData.date, {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
						</span>
					</div>
					<Divider />
					<div className="confirmation-item">
						<span className="confirmation-label">Time</span>
						<span className="confirmation-value">
							{appointmentData.start_time && DateTimeUtils.formatLocalTime(appointmentData.start_time)}
							{appointmentData.start_time && (
								<span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginLeft: "0.5rem" }}>
									({Intl.DateTimeFormat().resolvedOptions().timeZone})
								</span>
							)}
						</span>
					</div>
					<Divider />
					{selectedAppointmentType && (
						<>
							<div className="confirmation-item">
								<span className="confirmation-label">Duration</span>
								<span className="confirmation-value">{selectedAppointmentType.duration_minutes} minutes</span>
							</div>
							<Divider />
						</>
					)}
					<div className="confirmation-item">
						<span className="confirmation-label">Type</span>
						<span className="confirmation-value">{selectedAppointmentType?.telehealth_enabled ? "Online" : "In-person"}</span>
					</div>
					<Divider />
					<div className="confirmation-item">
						<span className="confirmation-label">Patient</span>
						<span className="confirmation-value">
							{patientInfo.first_name} {patientInfo.last_name}
						</span>
					</div>
					<Divider />
					<div className="confirmation-item">
						<span className="confirmation-label">Email</span>
						<span className="confirmation-value">{patientInfo.email}</span>
					</div>
					<Divider />
					<div className="confirmation-item">
						<span className="confirmation-label">Phone</span>
						<span className="confirmation-value">{patientInfo.phone}</span>
					</div>

					{requiresPayment() && financeEnabled && (
						<>
							<Divider />
							<div className="confirmation-item">
								<span className="confirmation-label">Payment Amount</span>
								<span className="confirmation-value" style={{ fontSize: "1.125rem", fontWeight: 600, color: "var(--primary)" }}>
									${price.toFixed(2)}
								</span>
							</div>
							{selectedPaymentMethodDetails && (
								<>
									<Divider />
									<div className="confirmation-item">
										<span className="confirmation-label">Payment Method</span>
										<span className="confirmation-value">
											{selectedPaymentMethodDetails.card?.brand.toUpperCase()} •••• {selectedPaymentMethodDetails.card?.last4}
										</span>
									</div>
								</>
							)}
						</>
					)}
				</Card>

				{requiresPayment() && financeEnabled && (
					<Alert
						message="Payment Authorization"
						description={`Your payment method will be charged $${price.toFixed(2)} upon confirmation. You will receive a receipt via email.`}
						type="info"
						showIcon
						icon={<CreditCardOutlined />}
						style={{ marginTop: "1rem" }}
					/>
				)}

				<div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
					<Button onClick={() => goToStep(requiresPayment() && financeEnabled ? 4 : 3)}>
						<i className="fas fa-arrow-left" /> Back
					</Button>
					<Button type="primary" loading={isSubmitting} onClick={handleSubmit}>
						{isSubmitting ? "Processing..." : requiresPayment() && financeEnabled ? "Pay & Confirm Booking" : "Confirm Booking"}
					</Button>
				</div>
			</div>
		);
	};

	const renderTimeSlotsPanel = () => {
		if (!selectedDate || availableTimesForDate.length === 0) {
			return (
				<Card className="booking-summary-card">
					<h2 className="summary-title">
						<i className="fas fa-clock" />
						Available Times
					</h2>
					<div className="empty-state">
						<i className="fas fa-calendar-day" />
						<p>{selectedDate ? "No times available for this date" : "Select a date to see available times"}</p>
					</div>
				</Card>
			);
		}

		return (
			<Card className="booking-summary-card">
				<h2 className="summary-title">
					<i className="fas fa-clock" />
					Available Times
				</h2>
				{/* Mobile-only: the calendar is hidden once a date is picked, so this is how a phone user
				    gets back to it. Hidden on desktop, where the calendar stays beside this panel. */}
				<Button
					className="mobile-change-date-btn"
					size="small"
					onClick={() => {
						setSelectedDate("");
						setSelectedTimeSlot(null);
					}}
				>
					<i className="fas fa-arrow-left" /> Change date
				</Button>
				<p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "0.25rem" }}>
					{parseLocalDateString(selectedDate).toLocaleDateString("en-US", {
						month: "long",
						day: "numeric",
						year: "numeric",
					})}
				</p>
				<p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginBottom: bookingPolicy?.timezone ? "0.25rem" : "1rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
					<i className="fas fa-globe" />
					Times shown in your local timezone: <strong>{Intl.DateTimeFormat().resolvedOptions().timeZone}</strong>
				</p>
				{/* The slots above stay in the PATIENT's timezone. This line describes the CLINIC's hours,
				    which is the one thing `policy.timezone` actually tells us. */}
				{bookingPolicy?.timezone && (
					<p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
						<i className="fas fa-clock" />
						Clinic hours are set in: <strong>{bookingPolicy.timezone}</strong>
					</p>
				)}

				<div className="time-slots-sidebar">
					{availableTimesForDate.map((slot) => {
						const isSelected = selectedTimeSlot?.time === slot.time;
						const practitionerCount = getPractitionerCountForTimeSlot(slot);

						return (
							<div key={`${slot.date}-${slot.time}`}>
								<div className={`time-slot-item ${isSelected ? "selected" : ""}`} onClick={() => handleTimeSlotSelect(slot)}>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
										<span style={{ fontWeight: 500 }}>{slot.display_time || formatLocalTime(slot.datetime)}</span>
										{bookingFlow === "time" && practitionerCount > 0 && (
											<Badge count={practitionerCount} style={{ backgroundColor: isSelected ? "white" : "var(--primary)" }} title={`${practitionerCount} consultant${practitionerCount !== 1 ? "s" : ""} available`} />
										)}
									</div>
								</div>

								{bookingFlow === "time" && isSelected && filteredPractitioners.length > 0 && (
									<div className="consultant-dropdown">
										<div
											style={{
												padding: "0.5rem 0.75rem",
												fontSize: "0.75rem",
												fontWeight: 600,
												color: "var(--text-secondary)",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
											}}
										>
											Select Consultant
										</div>
										{filteredPractitioners.map((practitioner) => renderConsultantCard(practitioner, true))}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</Card>
		);
	};

	const renderBookingSummary = () => {
		if (currentStep === 0) return null;

		if ((currentStep === 2 && bookingFlow === "consultant") || (currentStep === 1 && bookingFlow === "time")) {
			return renderTimeSlotsPanel();
		}

		const price = selectedAppointmentType?.default_price ? parseFloat(selectedAppointmentType.default_price) : 0;

		return (
			<Card className="booking-summary-card">
				<h2 className="summary-title">
					<i className="fas fa-calendar" />
					Booking Summary
				</h2>

				{!selectedAppointmentType ? (
					<div className="empty-state">
						<i className="fas fa-calendar-check" />
						<p>Select an appointment type to begin</p>
					</div>
				) : (
					<>
						<div className="summary-item">
							<span className="summary-label">Type</span>
							<span className="summary-value">{selectedAppointmentType.name}</span>
						</div>
						<Divider />
						<div className="summary-item">
							<span className="summary-label">Duration</span>
							<span className="summary-value">{selectedAppointmentType.duration_minutes} min</span>
						</div>
						<Divider />

						{bookingFlow === "time" && selectedDate && (
							<>
								<div className="summary-item">
									<span className="summary-label">Date</span>
									<span className="summary-value">
										{parseLocalDateString(selectedDate).toLocaleDateString("en-US", {
											month: "long",
											day: "numeric",
											year: "numeric",
										})}
									</span>
								</div>
								<Divider />
							</>
						)}
						{bookingFlow === "time" && selectedTimeSlot && (
							<>
								<div className="summary-item">
									<span className="summary-label">Time</span>
									<span className="summary-value">{selectedTimeSlot.display_time || formatLocalTime(selectedTimeSlot.datetime)}</span>
								</div>
								<Divider />
							</>
						)}
						{selectedPractitioner && (
							<>
								<div className="summary-item">
									<span className="summary-label">Consultant</span>
									<span className="summary-value">
										{selectedPractitioner.first_name} {selectedPractitioner.last_name}
									</span>
								</div>
								<Divider />
							</>
						)}
						{bookingFlow === "consultant" && selectedDate && (
							<>
								<div className="summary-item">
									<span className="summary-label">Date</span>
									<span className="summary-value">
										{parseLocalDateString(selectedDate).toLocaleDateString("en-US", {
											month: "long",
											day: "numeric",
											year: "numeric",
										})}
									</span>
								</div>
								<Divider />
							</>
						)}
						{bookingFlow === "consultant" && selectedTimeSlot && (
							<>
								<div className="summary-item">
									<span className="summary-label">Time</span>
									<span className="summary-value">{selectedTimeSlot.display_time || formatLocalTime(selectedTimeSlot.datetime)}</span>
								</div>
								<Divider />
							</>
						)}

						{price > 0 && (
							<>
								<div className="summary-item">
									<span className="summary-label">Fee</span>
									<span className="summary-value" style={{ fontSize: "1.125rem", fontWeight: 600, color: "var(--primary)" }}>
										${price.toFixed(2)}
									</span>
								</div>
								<Divider />
							</>
						)}
					</>
				)}

				{((currentStep === 2 && bookingFlow === "consultant") || (currentStep === 2 && bookingFlow === "time")) && canContinueToPatientInfo() && (
					<Button type="primary" style={{ width: "100%", marginTop: "1.5rem" }} onClick={() => goToStep(3)}>
						Continue <i className="fas fa-arrow-right" />
					</Button>
				)}
			</Card>
		);
	};

	const renderProfileModal = () => {
		if (!profileConsultant) return null;

		return (
			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
						<Avatar src={profileConsultant.profile_picture || "/assets/images/default-profile.png"} size={60} style={{ backgroundColor: "var(--primary)" }}>
							{`${profileConsultant.first_name[0]}${profileConsultant.last_name[0]}`}
						</Avatar>
						<div>
							<h2 style={{ margin: 0 }}>
								{profileConsultant.first_name} {profileConsultant.last_name}
							</h2>
							<p style={{ color: "var(--text-secondary)", margin: 0 }}>{profileConsultant.certifications}</p>
							<p style={{ color: "var(--text-secondary)", margin: 0 }}>{profileConsultant.primary_specialty}</p>
						</div>
					</div>
				}
				open={showProfileModal}
				onCancel={closeProfileModal}
				width={600}
				className="profile-modal"
				footer={
					<Button type="primary" style={{ width: "100%" }} onClick={bookFromProfile}>
						<i className="fas fa-calendar-check" style={{ color: "rgb(53, 162, 255)" }} /> Book Appointment
					</Button>
				}
			>
				<Divider></Divider>
				{profileConsultant.specialties && profileConsultant.specialties.length > 0 && (
					<div style={{ marginBottom: "1.5rem" }}>
						<h3>
							<i className="fas fa-star" style={{ color: "rgb(53, 162, 255)" }} /> Specializations
						</h3>
						<div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
							{profileConsultant.specialties.map((spec, idx) => (
								<Tag key={idx} color="blue">
									{spec}
								</Tag>
							))}
						</div>
					</div>
				)}

				{profileConsultant.biography && (
					<div style={{ marginBottom: "1.5rem" }}>
						<h3>
							<i className="fas fa-user" style={{ color: "rgb(53, 162, 255)" }} /> About
						</h3>
						<p style={{ color: "var(--text-secondary)" }}>{profileConsultant.biography}</p>
					</div>
				)}

				{profileConsultant.languages_spoken && profileConsultant.languages_spoken.length > 0 && (
					<div style={{ marginBottom: "1.5rem" }}>
						<h3>
							<i className="fas fa-globe" /> Languages
						</h3>
						<p style={{ color: "var(--text-secondary)" }}>{profileConsultant.languages_spoken.join(", ")}</p>
					</div>
				)}

				{profileConsultant.years_of_experience && (
					<div style={{ marginBottom: "1.5rem" }}>
						<h3>
							<i className="fas fa-briefcase" /> Experience
						</h3>
						<p style={{ color: "var(--text-secondary)" }}>{profileConsultant.years_of_experience} years</p>
					</div>
				)}
			</Modal>
		);
	};

	// ── Tokenised booking link outcomes (S9, ClickUp 86d3yhdw9) ─────────────────────────────
	// Both gates are `accessToken &&` on purpose: an anonymous visit can never reach either,
	// so the page a visitor without a link sees is byte-for-byte the page they saw before.
	// No <Layout> here or below — this route is deliberately chrome-free.
	if (accessToken && isLoadingLink) {
		return (
			<div className="patient-booking-form booking-container" data-testid="booking-link-loading">
				{contextHolder}
				<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (accessToken && linkState !== "ok") {
		const view = {
			already_booked: {
				testId: "booking-link-used",
				// Green tick, not a grey stop: this is the ONE refusal that is good news.
				icon: <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a", marginBottom: 16 }} />,
				// ⚠️ VERBATIM — the ticket's own requested wording. Do not paraphrase.
				copy: "This appointment has already been booked.",
			},
			expired: {
				testId: "booking-link-expired",
				icon: <StopOutlined style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 16 }} />,
				copy: "This booking link has expired. Please contact your clinic for a new one.",
			},
			invalid: {
				testId: "booking-link-invalid",
				icon: <StopOutlined style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 16 }} />,
				copy: "This booking link is no longer valid.",
			},
		}[linkState];

		return (
			<div className="patient-booking-form booking-container">
				{contextHolder}
				<Card className="success-card" data-testid={view.testId} style={{ textAlign: "center" }}>
					<div style={{ marginBottom: "1rem" }}>
						<BrandLogo height={32} />
					</div>
					{view.icon}
					<h2>{view.copy}</h2>
				</Card>
			</div>
		);
	}

	// Screening contraindication (S8). The message is clinic-authored per appointment type and is
	// rendered verbatim; which question or answer caused it is never sent to the browser.
	if (screeningBlocked) {
		return (
			<div className="patient-booking-form booking-container">
				{contextHolder}
				<Card className="success-card" data-testid="booking-screening-blocked" style={{ textAlign: "center" }}>
					<div style={{ marginBottom: "1rem" }}>
						<BrandLogo height={32} />
					</div>
					<StopOutlined style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 16 }} />
					<h2>{screeningBlocked}</h2>
					<Button type="primary" style={{ marginTop: "1.5rem" }} data-testid="booking-screening-choose-another" onClick={resetScreening}>
						Choose a different appointment type
					</Button>
				</Card>
			</div>
		);
	}

	if (isSubmitted) {
		const price = selectedAppointmentType?.default_price ? parseFloat(selectedAppointmentType.default_price) : 0;

		return (
			<div className="booking-container">
				{contextHolder}
				<Card className="success-card">
					<div className="success-icon">
						<i className="fas fa-check" />
					</div>
					<h2>Booking Confirmed!</h2>
					<p>Your appointment has been successfully booked.</p>
					{requiresPayment() && <p style={{ marginTop: "0.5rem", color: "var(--success)" }}>Payment of ${price.toFixed(2)} processed successfully.</p>}
					<p style={{ marginTop: "1rem" }}>You will receive a confirmation email shortly.</p>

					<Card className="confirmation-details">
						<p>
							<strong>Type:</strong> {selectedAppointmentType?.name}
						</p>
						<p>
							<strong>Provider:</strong> {selectedPractitioner?.first_name} {selectedPractitioner?.last_name}
						</p>
						<p>
							<strong>Date:</strong>{" "}
							{appointmentData.date &&
								DateTimeUtils.formatLocalDate(appointmentData.date, {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
						</p>
						<p>
							<strong>Time:</strong> {appointmentData.start_time && DateTimeUtils.formatLocalTime(appointmentData.start_time)}
						</p>
						<p>
							<strong>Duration:</strong> {selectedAppointmentType?.duration_minutes} minutes
						</p>
						<p>
							<strong>Type:</strong> {selectedAppointmentType?.telehealth_enabled ? "Online" : "In-person"}
						</p>
						<p>
							<strong>Category:</strong> {appointmentData.category}
						</p>
						{requiresPayment() && (
							<p>
								<strong>Amount Paid:</strong> ${price.toFixed(2)}
							</p>
						)}
						<p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "1rem" }}>
							Timezone: {DateTimeUtils.getUserTimezone()} ({DateTimeUtils.getTimezoneOffset()})
						</p>
					</Card>

					<Button type="primary" style={{ width: "100%", marginTop: "1.5rem" }} onClick={() => window.location.reload()}>
						Book Another Appointment
					</Button>
				</Card>
			</div>
		);
	}

	// On mobile the calendar and the times panel share one column and swap in place: the calendar
	// shows until a date is picked, then the times. This flag scopes that swap to the date/time step
	// so the CSS toggle can never hide the selection column on the consultant/patient/payment steps.
	const isDateTimeStep = (bookingFlow === "consultant" && currentStep === 2) || (bookingFlow === "time" && currentStep === 1);

	return (
		<div className="patient-booking-form booking-container">
			{contextHolder}

			<div className="booking-header">
				<div className="header-content">
					<BrandLogo height={40} />
					<div className="header-info">
						<h1>Book Your Consultation</h1>
						<p>Select your appointment type and preferred consultant</p>
					</div>
					<div style={{ textAlign: "right" }}>
						<p style={{ margin: "0" }}>Already have an account?</p>
						<Button
							type="primary"
							style={{
								zIndex: 10,
							}}
							onClick={() => (window.location.href = "/login")}
						>
							<i className="fas fa-sign-in-alt" style={{ marginRight: "0.5rem" }} />
							Login
						</Button>
					</div>
				</div>
			</div>

			<div className="booking-content">
				{currentStep === 0 ? (
					renderAppointmentTypeSelection()
				) : screeningRequired ? (
					renderScreeningStep()
				) : !bookingFlow ? (
					renderFlowSelection()
				) : (
					<>
						{renderProgressBar()}

						{renderErrorCard()}

						<div className={`booking-main${isDateTimeStep ? " date-time-step" : ""}${selectedDate ? " has-date" : ""}`}>
							<div className="booking-selection">
								<Button
									size="small"
									style={{ position: "absolute", top: "-3rem", right: 0 }}
									onClick={() => {
										setBookingFlow(null);
										setCurrentStep(1);
										setSelectedPractitioner(null);
										setSelectedDate("");
										setSelectedTimeSlot(null);
										setFilteredPractitioners([]);
									}}
								>
									<i className="fas fa-arrow-left" /> Change Booking Method
								</Button>

								{bookingFlow === "consultant" && currentStep === 1 && renderConsultantSelection()}
								{bookingFlow === "consultant" && currentStep === 2 && renderDateTimeSelection()}
								{bookingFlow === "time" && currentStep === 1 && renderDateTimeSelection()}
								{bookingFlow === "time" && currentStep === 2 && renderConsultantSelection()}
								{currentStep === 3 && renderPatientForm()}
								{currentStep === 4 && requiresPayment() && financeEnabled && renderPaymentSection()}
								{((currentStep === 4 && !requiresPayment()) || (currentStep === 4 && !financeEnabled) || currentStep === 5) && renderConfirmation()}
							</div>

							{renderBookingSummary()}
						</div>
					</>
				)}
			</div>

			{renderProfileModal()}

			<div className="booking-footer">
				{/* S8 (86d3w1g8u). `brandDisplayName` falls back to "Xoe Med", so a clinic that
				    is not whitelabelled — and a whitelabelled clinic that uploaded a logo but
				    never set a name — both read "© Xoe Med" exactly as today, never "© null". */}
				<p>&copy; {brandDisplayName}</p>
				{/* Co-branding (D7). Renders nothing when the clinic is not whitelabelled, so
				    the footer stays the single line it is today. */}
				<PoweredBy style={{ display: "block", marginTop: "0.35rem" }} />
			</div>
		</div>
	);
};

export default PatientBookingForm;
